"use client";

import { FormEvent, useCallback, useEffect, useState } from "react";
import { CalendarCheck, LifeBuoy } from "lucide-react";
import {
  AftercarePackage,
  CheckinMilestone,
  SupportType,
  WellbeingStatus,
  getAftercare,
  saveAftercareCheckin,
} from "@/features/care/api";

const milestoneLabels: Record<CheckinMilestone, string> = {
  DAY_3: "3일",
  WEEK_2: "2주",
  MONTH_3: "3개월",
};
const wellbeingLabels: Record<WellbeingStatus, string> = {
  GOOD: "잘 적응하고 있어요",
  WATCH: "조금 더 지켜보고 있어요",
  NEEDS_HELP: "도움이 필요해요",
};
const supportLabels: Record<SupportType, string> = {
  NONE: "지원 요청 없음",
  RETURN: "반환 상담 요청",
  REHOMING: "재배치 상담 요청",
};

export function AftercareCheckins({ orderId }: { orderId: string }) {
  const [data, setData] = useState<AftercarePackage | null>(null);
  const [editing, setEditing] = useState<CheckinMilestone | null>(null);
  const [wellbeing, setWellbeing] = useState<WellbeingStatus>("GOOD");
  const [note, setNote] = useState("");
  const [supportType, setSupportType] = useState<SupportType>("NONE");
  const [supportDetails, setSupportDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      getAftercare(orderId)
        .then(setData)
        .catch(() => setError("check-in을 불러오지 못했습니다.")),
    [orderId],
  );
  useEffect(() => {
    void load();
  }, [load]);

  function open(milestone: CheckinMilestone) {
    const current = data?.milestones.find(
      (item) => item.milestone === milestone,
    )?.checkin;
    setEditing(milestone);
    setWellbeing(current?.wellbeing ?? "GOOD");
    setNote(current?.note ?? "");
    setSupportType(current?.supportType ?? "NONE");
    setSupportDetails(current?.supportDetails ?? "");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError("");
    try {
      await saveAftercareCheckin(orderId, {
        milestone: editing,
        wellbeing,
        note,
        supportType,
        supportDetails: supportType === "NONE" ? undefined : supportDetails,
      });
      setEditing(null);
      await load();
    } catch {
      setError("10자 이상의 근황과 지원 요청 내용을 확인해주세요.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="aftercare-section">
      <header>
        <CalendarCheck />
        <div>
          <h2>적응 check-in</h2>
          <p>
            인도 후 3일·2주·3개월의 생활 변화를 기록하세요. 예정일 전에도 기록할
            수 있습니다.
          </p>
        </div>
      </header>
      <div className="aftercare-milestones">
        {data?.milestones.map((item) => (
          <article key={item.milestone}>
            <span>{milestoneLabels[item.milestone]} check-in</span>
            <small>
              권장일 {new Date(item.dueAt).toLocaleDateString("ko-KR")}
            </small>
            {item.checkin ? (
              <>
                <b>{wellbeingLabels[item.checkin.wellbeing]}</b>
                <p>{item.checkin.note}</p>
                {item.checkin.supportType !== "NONE" && (
                  <em>
                    {supportLabels[item.checkin.supportType]} ·{" "}
                    {item.checkin.supportStatus}
                  </em>
                )}
              </>
            ) : (
              <p>아직 기록이 없습니다.</p>
            )}
            <button type="button" onClick={() => open(item.milestone)}>
              {item.checkin ? "기록 수정" : "근황 기록"}
            </button>
          </article>
        ))}
      </div>
      {editing && (
        <form className="aftercare-form" onSubmit={submit}>
          <h3>{milestoneLabels[editing]} check-in</h3>
          <label>
            현재 적응 상태
            <select
              value={wellbeing}
              onChange={(event) =>
                setWellbeing(event.target.value as WellbeingStatus)
              }
            >
              {Object.entries(wellbeingLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            근황
            <textarea
              required
              minLength={10}
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <label>
            지원 요청
            <select
              value={supportType}
              onChange={(event) =>
                setSupportType(event.target.value as SupportType)
              }
            >
              {Object.entries(supportLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          {supportType !== "NONE" && (
            <label>
              상담이 필요한 내용
              <textarea
                required
                minLength={10}
                maxLength={2000}
                value={supportDetails}
                onChange={(event) => setSupportDetails(event.target.value)}
              />
            </label>
          )}
          <div>
            <button disabled={busy}>{busy ? "저장 중" : "저장"}</button>
            <button type="button" onClick={() => setEditing(null)}>
              취소
            </button>
          </div>
        </form>
      )}
      {data?.returnPolicy && (
        <aside className="return-policy">
          <LifeBuoy />
          <div>
            <strong>브리더의 반환·재배치 정책</strong>
            <p>{data.returnPolicy}</p>
            <small>
              브리더가 작성한 정책 안내이며 자동 승인이나 법적 판단이 아닙니다.
              긴급한 건강 문제는 동물병원에 먼저 연락하세요.
            </small>
          </div>
        </aside>
      )}
      {error && (
        <p className="care-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
